import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, UserPlus, Phone, CheckCircle2, AlertCircle, Building2, FileQuestion } from 'lucide-react';
import { BaseEntityModal } from './BaseEntityModal';
import { MunicipalityField, DateField, PriorityField, BeneficiariesField, ReunionAssignmentField } from './shared/FormFields';
import { AuditWatermark } from './shared/AuditWatermark';
import { GestionModalProps, Gestion } from '../../types';
import { createGestion, updateGestion, deleteGestion } from '../../lib/api/gestiones';
import { getReunionTitleById, getReunionesForDropdown, ReunionOption } from '../../lib/api/reuniones';
import { getMunicipalityId, getReunionIdByTitle } from '../../lib/adapters';
import { getMunicipalityByName } from '../../lib/api/municipalities';
import { getMunicipalityIdStatic } from '../../lib/municipalities-static';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useToast } from '../../contexts/ToastContext';
import { sanitizeText, sanitizeTextarea, sanitizePhone } from '../../lib/validation/input';
import { logger } from '../../lib/logger';
import { queryCache } from '../../lib/cache/queryCache';

const ENTITY_COLOR = '#B89658';

export const GestionModal: React.FC<GestionModalProps> = ({
  isOpen,
  onClose,
  municipalities,
  initialDate,
  preselectedReunion,
  initialData,
  onSave,
  sourceReunion,
  onBackToReunion,
}) => {
  const { currentOrg } = useOrganization();
  const toast = useToast();

  // Form state (isEditing is now controlled by BaseEntityModal)
  const [solicitantName, setSolicitantName] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedDate, setSelectedDate] = useState(initialDate || new Date().toISOString().split('T')[0]);
  const [selectedMunicipality, setSelectedMunicipality] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'Registrada' | 'Completada'>('Registrada');
  const [prioridad, setPrioridad] = useState<'Alta' | 'Media' | 'Baja' | null>(null);
  const [numeroBeneficiarios, setNumeroBeneficiarios] = useState<number | null>(null);
  const [organizacion, setOrganizacion] = useState('');
  const [solicitud, setSolicitud] = useState('');

  // Reunion assignment
  const [isAssignedToReunion, setIsAssignedToReunion] = useState(false);
  const [selectedReunionId, setSelectedReunionId] = useState<string | null>(null);
  const [reunionOptions, setReunionOptions] = useState<ReunionOption[]>([]);
  const [reunionOptionsLoading, setReunionOptionsLoading] = useState(false);
  const [preselectedReunionTitle, setPreselectedReunionTitle] = useState('');

  // Refs to ensure handleSave always has access to latest reunion state values
  // This fixes the stale closure issue where React state updates may not have propagated
  // by the time handleSave is called from BaseEntityModal
  const isAssignedToReunionRef = useRef(isAssignedToReunion);
  const selectedReunionIdRef = useRef(selectedReunionId);
  const preselectedReunionTitleRef = useRef(preselectedReunionTitle);

  // Keep refs in sync with state
  useEffect(() => {
    isAssignedToReunionRef.current = isAssignedToReunion;
  }, [isAssignedToReunion]);

  useEffect(() => {
    selectedReunionIdRef.current = selectedReunionId;
  }, [selectedReunionId]);

  useEffect(() => {
    preselectedReunionTitleRef.current = preselectedReunionTitle;
  }, [preselectedReunionTitle]);

  // Confirmation modals
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  // Initialize form from props
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        // Edit mode - populate form with existing data
        setSolicitantName(initialData.solicitant);
        setPhone(initialData.phone);
        setSelectedDate(initialData.date);
        setSelectedMunicipality(initialData.municipality);
        setDescription(initialData.description);
        setStatus(initialData.status);
        setPrioridad(initialData.prioridad ?? null);
        setNumeroBeneficiarios(initialData.numero_beneficiarios ?? null);
        setOrganizacion(initialData.organizacion || '');
        setSolicitud(initialData.solicitud || '');

        // Reunion link
        if (initialData.linkedReunionDbId) {
          setIsAssignedToReunion(true);
          setSelectedReunionId(initialData.linkedReunionDbId);

          if (initialData.linkedReunionTitle) {
            setPreselectedReunionTitle(initialData.linkedReunionTitle);
          } else {
            // Fetch title
            getReunionTitleById(initialData.linkedReunionDbId)
              .then((title) => setPreselectedReunionTitle(title || 'Reunión vinculada'))
              .catch(() => setPreselectedReunionTitle('Reunión vinculada'));
          }
        } else {
          setIsAssignedToReunion(false);
          setSelectedReunionId(null);
        }
      } else {
        // New mode - reset form
        setSolicitantName('');
        setPhone('');
        setSelectedMunicipality('');
        setDescription('');
        setStatus('Registrada');
        setPrioridad(null);
        setNumeroBeneficiarios(null);
        setOrganizacion('');
        setSolicitud('');

        if (initialDate) setSelectedDate(initialDate);

        // Preselected reunion
        if (preselectedReunion) {
          setIsAssignedToReunion(true);
          setSelectedReunionId(preselectedReunion.id || null);
          setPreselectedReunionTitle(preselectedReunion.title);
          if (preselectedReunion.municipality) {
            setSelectedMunicipality(preselectedReunion.municipality);
          }
        } else {
          setIsAssignedToReunion(false);
          setSelectedReunionId(null);
        }
      }
    }
  }, [isOpen, initialData, initialDate, preselectedReunion]);

  // Load reunion options when assigned
  useEffect(() => {
    if (isAssignedToReunion && reunionOptions.length === 0 && !reunionOptionsLoading) {
      setReunionOptionsLoading(true);
      getReunionesForDropdown()
        .then((options) => {
          setReunionOptions(options);
          setReunionOptionsLoading(false);
        })
        .catch((err) => {
          logger.error('[GestionModal] Error loading reunion options:', err);
          setReunionOptionsLoading(false);
        });
    }
  }, [isAssignedToReunion, reunionOptions.length, reunionOptionsLoading]);

  const handleStatusChange = (newStatus: 'Registrada' | 'Completada') => {
    if (newStatus === 'Completada' && status !== 'Completada') {
      setIsConfirmOpen(true);
    } else {
      setStatus(newStatus);
    }
  };

  const confirmCompletion = async () => {
    if (!initialData || !initialData.dbId) {
      setIsConfirmOpen(false);
      return;
    }

    try {
      setStatus('Completada');
      await updateGestion(initialData.dbId, { status: 'Completada' });
      // Invalidate cache to ensure UI reflects the status change
      if (currentOrg?.id) {
        queryCache.invalidate(`gestiones|org:${currentOrg.id}`);
        queryCache.invalidate(`gestiones-paginated|org:${currentOrg.id}`);
      }
      setIsConfirmOpen(false);
      if (onSave) onSave();
      onClose();
    } catch (error) {
      logger.error('[GestionModal] Error marking as completed:', error);
      toast.error('Error al actualizar', 'No se pudo marcar como completada. Por favor intenta de nuevo.');
      setIsConfirmOpen(false);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!selectedMunicipality) {
      toast.warning('Campos incompletos', 'Por favor selecciona un municipio');
      throw new Error('Validation failed');
    }

    if (!solicitantName || !phone || !selectedDate) {
      toast.warning('Campos incompletos', 'Por favor completa todos los campos requeridos');
      throw new Error('Validation failed');
    }

    // Resolve municipality ID
    let municipalityId = getMunicipalityId(selectedMunicipality);
    if (!municipalityId) municipalityId = getMunicipalityIdStatic(selectedMunicipality);
    if (!municipalityId) {
      try {
        const municipality = await getMunicipalityByName(selectedMunicipality);
        municipalityId = municipality?.id || null;
      } catch (err) {
        logger.warn('[GestionModal] Municipality lookup failed', err);
      }
    }

    if (!municipalityId) {
      toast.error('Error de datos', 'No se pudo encontrar el municipio. Verifica la conexión.');
      throw new Error('Municipality not found');
    }

    if (!currentOrg?.id) {
      toast.error('Error de sesión', 'No se pudo obtener la organización actual');
      throw new Error('Organization not found');
    }

    // Resolve reunion ID using refs to ensure we have the latest values
    // This fixes the stale closure issue where state values might be outdated
    const currentIsAssigned = isAssignedToReunionRef.current;
    const currentSelectedId = selectedReunionIdRef.current;
    const currentPreselectedTitle = preselectedReunionTitleRef.current;

    logger.log('[GestionModal] handleSave - Reunion state (from refs):', {
      isAssignedToReunion: currentIsAssigned,
      selectedReunionId: currentSelectedId,
      preselectedReunionTitle: currentPreselectedTitle,
    });
    const resolvedReunionId = currentIsAssigned ? (currentSelectedId || getReunionIdByTitle(currentPreselectedTitle)) : null;
    logger.log('[GestionModal] handleSave - resolvedReunionId:', resolvedReunionId);

    const gestionData = {
      solicitant: solicitantName,
      phone,
      municipality_id: municipalityId,
      organization_id: currentOrg.id,
      date: selectedDate,
      status,
      description,
      reunion_id: resolvedReunionId,
      prioridad,
      numero_beneficiarios: numeroBeneficiarios,
      organizacion: sanitizeText(organizacion, 500).trim() || null,
      solicitud: sanitizeText(solicitud, 500).trim() || null,
    };

    try {
      if (initialData && initialData.dbId) {
        await updateGestion(initialData.dbId, gestionData);
      } else {
        await createGestion(gestionData);
      }
      // Invalidate cache to ensure UI reflects the changes
      if (currentOrg?.id) {
        queryCache.invalidate(`gestiones|org:${currentOrg.id}`);
        queryCache.invalidate(`gestiones-paginated|org:${currentOrg.id}`);
      }
    } catch (error) {
      logger.error('[GestionModal] Error saving gestion:', error);
      toast.error('Error al guardar', 'No se pudo guardar la gestión. Por favor intenta de nuevo.');
      throw error;
    }
  };

  const handleDelete = async (id: string) => {
    await deleteGestion(id);
    // Invalidate cache to ensure UI reflects the deletion
    if (currentOrg?.id) {
      queryCache.invalidate(`gestiones|org:${currentOrg.id}`);
      queryCache.invalidate(`gestiones-paginated|org:${currentOrg.id}`);
    }
  };

  const handleReunionSelect = (reunionId: string | null) => {
    logger.log('[GestionModal] handleReunionSelect called with:', reunionId);
    setSelectedReunionId(reunionId);
    // Auto-fill municipality from selected reunion
    if (reunionId) {
      const reunion = reunionOptions.find((r) => r.id === reunionId);
      if (reunion?.municipality_name) {
        setSelectedMunicipality(reunion.municipality_name);
      }
    }
  };

  return (
    <>
      <BaseEntityModal<Gestion>
        isOpen={isOpen}
        onClose={onClose}
        initialData={initialData}
        onSave={onSave}
        onDelete={handleDelete}
        entityType="gestion"
        entityIcon={FileText}
        entityColor={ENTITY_COLOR}
        createTitle="Nueva Gestión"
        editTitle="Detalles de la Gestión"
        createSubtitle="Registre los detalles del trámite."
        editSubtitle="Información registrada de la gestión."
        saveButtonText="Guardar Gestión"
        onSaveClick={handleSave}
        showBackButton={!!sourceReunion && !!onBackToReunion}
        onBackClick={onBackToReunion}
        backButtonTitle="Volver a la reunión"
        entityId={initialData?.dbId}
      >
        {(isEditing) => (
          <>
            {/* Status Toggle (only for existing gestiones) */}
            {initialData && (
              <div className="col-span-2 mb-2">
                <label className="block text-xs font-bold text-[#4B5563] uppercase tracking-wide mb-2">Estatus</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleStatusChange('Registrada')}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                      status === 'Registrada' ? 'bg-[#B89658] text-white shadow-md' : 'bg-[#F3F4F6] text-[#6B7280] hover:bg-[#E5E7EB]'
                    }`}
                  >
                    <AlertCircle size={16} />
                    Registrada
                  </button>
                  <button
                    type="button"
                    onClick={() => handleStatusChange('Completada')}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                      status === 'Completada' ? 'bg-[#059669] text-white shadow-md' : 'bg-[#F3F4F6] text-[#6B7280] hover:bg-[#E5E7EB]'
                    }`}
                  >
                    <CheckCircle2 size={16} />
                    Completada
                  </button>
                </div>
              </div>
            )}

            {/* Solicitant Name */}
            <div className="col-span-2 lg:col-span-1">
              <label className="block text-xs font-bold text-[#4B5563] uppercase tracking-wide mb-2">
                Solicitante <span className="text-[#5D1828]">*</span>
              </label>
              <div className="relative">
                <UserPlus size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280]" />
                {isEditing ? (
                  <input
                    type="text"
                    value={solicitantName}
                    onChange={(e) => setSolicitantName(sanitizeText(e.target.value, 200))}
                    placeholder="Nombre del ciudadano o solicitante..."
                    className="w-full pl-11 pr-4 py-3 bg-white border border-[#D1D5DB] rounded-lg text-[#111827] text-sm outline-none focus:border-[#B89658] focus:ring-1 focus:ring-[#B89658] transition-all placeholder:text-[#9CA3AF]"
                    maxLength={200}
                  />
                ) : (
                  <div className="text-[#111827] font-medium flex items-center gap-2 p-3 pl-11 bg-[#F9FAFB] rounded-lg border border-[#D1D5DB]">
                    {solicitantName}
                  </div>
                )}
              </div>
            </div>

            {/* Phone Number */}
            <div className="col-span-2 lg:col-span-1">
              <label className="block text-xs font-bold text-[#4B5563] uppercase tracking-wide mb-2">
                Teléfono <span className="text-[#5D1828]">*</span>
              </label>
              <div className="relative">
                <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280]" />
                {isEditing ? (
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(sanitizePhone(e.target.value))}
                    placeholder="614-123-4567"
                    className="w-full pl-11 pr-4 py-3 bg-white border border-[#D1D5DB] rounded-lg text-[#111827] text-sm outline-none focus:border-[#B89658] focus:ring-1 focus:ring-[#B89658] transition-all placeholder:text-[#9CA3AF]"
                    maxLength={20}
                  />
                ) : (
                  <div className="text-[#111827] font-medium flex items-center gap-2 p-3 pl-11 bg-[#F9FAFB] rounded-lg border border-[#D1D5DB]">
                    {phone}
                  </div>
                )}
              </div>
            </div>

            {/* Organizacion Field */}
            <div className="col-span-2 lg:col-span-1">
              <label className="block text-xs font-bold text-[#4B5563] uppercase tracking-wide mb-2">
                Organización (Opcional)
              </label>
              <div className="relative">
                <Building2 size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280]" />
                {isEditing ? (
                  <input
                    type="text"
                    value={organizacion}
                    onChange={(e) => setOrganizacion(sanitizeText(e.target.value, 500))}
                    placeholder="Nombre de la organización..."
                    className="w-full pl-11 pr-4 py-3 bg-white border border-[#D1D5DB] rounded-lg text-[#111827] text-sm outline-none focus:border-[#B89658] focus:ring-1 focus:ring-[#B89658] transition-all placeholder:text-[#9CA3AF]"
                    maxLength={500}
                  />
                ) : (
                  <div className="text-[#111827] font-medium flex items-center gap-2 p-3 pl-11 bg-[#F9FAFB] rounded-lg border border-[#D1D5DB]">
                    {organizacion || <span className="text-[#9CA3AF]">Sin organización</span>}
                  </div>
                )}
              </div>
            </div>

            {/* Solicitud Field */}
            <div className="col-span-2 lg:col-span-1">
              <label className="block text-xs font-bold text-[#4B5563] uppercase tracking-wide mb-2">
                Solicitud (Opcional)
              </label>
              <div className="relative">
                <FileQuestion size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280]" />
                {isEditing ? (
                  <input
                    type="text"
                    value={solicitud}
                    onChange={(e) => setSolicitud(sanitizeText(e.target.value, 500))}
                    placeholder="Breve descripción de la solicitud..."
                    className="w-full pl-11 pr-4 py-3 bg-white border border-[#D1D5DB] rounded-lg text-[#111827] text-sm outline-none focus:border-[#B89658] focus:ring-1 focus:ring-[#B89658] transition-all placeholder:text-[#9CA3AF]"
                    maxLength={500}
                  />
                ) : (
                  <div className="text-[#111827] font-medium flex items-center gap-2 p-3 pl-11 bg-[#F9FAFB] rounded-lg border border-[#D1D5DB]">
                    {solicitud || <span className="text-[#9CA3AF]">Sin solicitud</span>}
                  </div>
                )}
              </div>
            </div>

            {/* Date Field */}
            <div className="col-span-2 lg:col-span-1">
              <DateField value={selectedDate} onChange={setSelectedDate} isEditing={isEditing} accentColor={ENTITY_COLOR} />
            </div>

            {/* Municipality Field */}
            <div className="col-span-2 lg:col-span-1">
              <MunicipalityField
                value={selectedMunicipality}
                onChange={setSelectedMunicipality}
                municipalities={municipalities}
                isEditing={isEditing}
                accentColor={ENTITY_COLOR}
              />
            </div>

            {/* Priority */}
            <div className="col-span-2 lg:col-span-1">
              <PriorityField value={prioridad} onChange={setPrioridad} isEditing={isEditing} />
            </div>

            {/* Beneficiaries */}
            <div className="col-span-2 lg:col-span-1">
              <BeneficiariesField value={numeroBeneficiarios} onChange={setNumeroBeneficiarios} isEditing={isEditing} />
            </div>

            {/* Reunion Assignment */}
            <ReunionAssignmentField
              isAssigned={isAssignedToReunion}
              onToggle={setIsAssignedToReunion}
              selectedReunionId={selectedReunionId}
              onSelectReunion={handleReunionSelect}
              reunionOptions={reunionOptions}
              reunionOptionsLoading={reunionOptionsLoading}
              isEditing={isEditing}
              accentColor={ENTITY_COLOR}
              preselectedTitle={preselectedReunionTitle}
            />

            {/* Description */}
            <div className="col-span-2">
              <label className="block text-xs font-bold text-[#4B5563] uppercase tracking-wide mb-2">Detalle de la Gestión</label>
              <div className="relative">
                {isEditing ? (
                  <textarea
                    rows={6}
                    value={description}
                    onChange={(e) => setDescription(sanitizeTextarea(e.target.value, 5000))}
                    placeholder="Describa detalladamente la gestión o trámite solicitado..."
                    className="w-full p-4 bg-white border border-[#D1D5DB] rounded-lg text-[#111827] text-base outline-none focus:border-[#B89658] focus:ring-1 focus:ring-[#B89658] transition-all placeholder:text-[#9CA3AF] resize-none"
                    maxLength={5000}
                  />
                ) : (
                  <div className="w-full p-4 bg-[#F9FAFB] border border-[#D1D5DB] rounded-lg text-[#111827] text-sm min-h-[120px] whitespace-pre-wrap max-h-[200px] overflow-y-auto">
                    {description || 'Sin descripción detallada.'}
                  </div>
                )}
              </div>
            </div>

            {/* Audit Watermark (view mode only) */}
            {!isEditing && initialData && (
              <AuditWatermark
                createdBy={initialData.created_by}
                createdAt={initialData.created_at}
                completedBy={initialData.completed_by}
                completedAt={initialData.completed_at}
                entityType="gestion"
              />
            )}
          </>
        )}
      </BaseEntityModal>

      {/* Completion Confirmation Modal */}
      <AnimatePresence>
        {isConfirmOpen && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsConfirmOpen(false)}
              className="absolute inset-0 bg-[#111827]/60 backdrop-blur-sm"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 10 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-sm relative z-50 p-6 flex flex-col items-center text-center"
            >
              <div className="w-16 h-16 rounded-full bg-[#059669]/10 flex items-center justify-center mb-4 text-[#059669]">
                <CheckCircle2 size={32} />
              </div>
              <h3 className="text-xl font-bold text-[#111827] mb-2">¿Marcar como completada?</h3>
              <p className="text-[#6B7280] text-sm mb-6">
                Al confirmar, la gestión cambiará su estatus a "Completada" y se registrará la fecha de cierre.
              </p>
              <div className="flex w-full gap-3">
                <button
                  onClick={() => setIsConfirmOpen(false)}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-[#D1D5DB] text-[#4B5563] font-medium hover:bg-[#F9FAFB] transition-colors text-sm"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmCompletion}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-[#059669] text-white font-medium hover:bg-[#059669]/90 shadow-md shadow-[#059669]/30 transition-all text-sm"
                >
                  Confirmar
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
